import React, { useEffect, useState, useMemo } from 'react';
import Layout from '../components/Layout';
import api from '../api/client';
import { Table as TableIcon, Plus, FloppyDisk, Trash, MagnifyingGlass, X } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const COLS = [
  { key: 'name', label: 'Name', type: 'text', w: 'w-44' },
  { key: 'date_of_birth', label: 'DOB', type: 'date', w: 'w-36' },
  { key: 'joining_date', label: 'Joined', type: 'date', w: 'w-36' },
  { key: 'phone', label: 'Phone', type: 'text', w: 'w-32' },
  { key: 'email', label: 'Email', type: 'text', w: 'w-48' },
  { key: 'batch_id', label: 'Batch', type: 'batch', w: 'w-40' },
  { key: 'parent_name', label: 'Parent', type: 'text', w: 'w-40' },
  { key: 'parent_phone', label: 'Parent Phone', type: 'text', w: 'w-32' },
  { key: 'payment_status', label: 'Status', type: 'status', w: 'w-28' },
  { key: 'is_tutor', label: 'Tutor', type: 'bool', w: 'w-16' },
  { key: 'is_active', label: 'Active', type: 'bool', w: 'w-16' },
];

const blankRow = {
  name: '', date_of_birth: '', joining_date: '', phone: '', email: '',
  batch_id: '', parent_name: '', parent_phone: '', payment_status: 'pending',
  is_tutor: false, is_active: true,
};

function StudentsSheet() {
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState('');
  const [newRow, setNewRow] = useState(null);

  useEffect(() => {
    Promise.all([api.get('/students'), api.get('/batches')])
      .then(([sRes, bRes]) => {
        setStudents(sRes.data);
        setBatches(bRes.data);
      })
      .catch(() => toast.error('Failed to load data'))
      .finally(() => setLoading(false));
  }, []);

  const batchName = (id) => batches.find((b) => b.id === id)?.name || '';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? students.filter((s) =>
          (s.name || '').toLowerCase().includes(q) ||
          (s.phone || '').toLowerCase().includes(q) ||
          batchName(s.batch_id).toLowerCase().includes(q))
      : students;
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [students, search, batches]);

  const updateCell = (id, key, value) => {
    setStudents((prev) => prev.map((s) => (s.id === id ? { ...s, [key]: value } : s)));
    setDirty((prev) => ({ ...prev, [id]: true }));
  };

  const saveRow = async (student) => {
    setSavingId(student.id);
    try {
      const payload = {
        name: student.name,
        date_of_birth: student.date_of_birth || '',
        joining_date: student.joining_date || '',
        phone: student.phone || '',
        email: student.email || '',
        batch_id: student.batch_id || '',
        parent_name: student.parent_name || '',
        parent_phone: student.parent_phone || '',
        payment_status: student.payment_status || 'pending',
        is_tutor: !!student.is_tutor,
        is_active: !!student.is_active,
      };
      await api.put(`/students/${student.id}`, payload);
      setDirty((prev) => { const n = { ...prev }; delete n[student.id]; return n; });
      toast.success(`Saved ${student.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingId(null);
    }
  };

  const deleteRow = async (student) => {
    if (!window.confirm(`Delete ${student.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/students/${student.id}`);
      setStudents((prev) => prev.filter((s) => s.id !== student.id));
      toast.success(`Deleted ${student.name}`);
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const saveNew = async () => {
    if (!newRow.name.trim()) { toast.error('Name is required'); return; }
    if (!newRow.batch_id) { toast.error('Please select a batch'); return; }
    setSavingId('new');
    try {
      const res = await api.post('/students/quick-add', {
        name: newRow.name,
        date_of_birth: newRow.date_of_birth || '',
        joining_date: newRow.joining_date || '',
        phone: newRow.phone || '',
        email: newRow.email || '',
        batch_id: newRow.batch_id,
        parent_name: newRow.parent_name || '',
        parent_phone: newRow.parent_phone || '',
        payment_status: newRow.payment_status || 'pending',
        is_tutor: !!newRow.is_tutor,
      });
      setStudents((prev) => [...prev, res.data]);
      setNewRow(null);
      toast.success('Student added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add');
    } finally {
      setSavingId(null);
    }
  };

  const renderCell = (row, col, onChange) => {
    const val = row[col.key];
    const base = 'h-8 px-2 py-1 text-sm bg-transparent border border-transparent hover:border-slate-700 focus:border-blue-500 rounded text-white w-full';
    if (col.type === 'bool') {
      return (
        <input
          type="checkbox"
          checked={!!val}
          onChange={(e) => onChange(col.key, e.target.checked)}
          className="w-4 h-4 accent-blue-500 cursor-pointer"
          data-testid={`cell-${col.key}`}
        />
      );
    }
    if (col.type === 'batch') {
      return (
        <select
          value={val || ''}
          onChange={(e) => onChange(col.key, e.target.value)}
          className={base + ' cursor-pointer'}
          data-testid={`cell-${col.key}`}
        >
          <option value="" className="bg-slate-900">—</option>
          {batches.map((b) => (
            <option key={b.id} value={b.id} className="bg-slate-900">{b.name}</option>
          ))}
        </select>
      );
    }
    if (col.type === 'status') {
      return (
        <select
          value={val || 'pending'}
          onChange={(e) => onChange(col.key, e.target.value)}
          className={base + ' cursor-pointer'}
          data-testid={`cell-${col.key}`}
        >
          <option value="pending" className="bg-slate-900">pending</option>
          <option value="paid" className="bg-slate-900">paid</option>
        </select>
      );
    }
    return (
      <input
        type={col.type}
        value={val || ''}
        onChange={(e) => onChange(col.key, e.target.value)}
        className={base}
        data-testid={`cell-${col.key}`}
      />
    );
  };

  return (
    <Layout>
      <div className="px-6 sm:px-8 lg:px-12 py-8" data-testid="students-sheet-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <TableIcon size={28} weight="duotone" className="text-blue-400" />
              <h1 className="text-3xl font-black tracking-tight text-white">Student Spreadsheet</h1>
            </div>
            <p className="text-sm text-slate-400 mt-1">Edit any field inline, then click save on the row. {filtered.length} students.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, phone, batch"
                className="pl-9 w-64 bg-slate-900 border-slate-700 text-white"
                data-testid="sheet-search"
              />
            </div>
            <Button
              onClick={() => setNewRow({ ...blankRow })}
              disabled={!!newRow}
              data-testid="add-row-btn"
              className="bg-blue-600 hover:bg-blue-500 text-white shrink-0"
            >
              <Plus size={16} weight="bold" className="mr-1.5" /> Add Student
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-slate-400" data-testid="sheet-loading">Loading...</p>
        ) : (
          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-auto">
            <table className="min-w-full text-left">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-950/50">
                  {COLS.map((c) => (
                    <th key={c.key} className={`px-3 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400 ${c.w}`}>{c.label}</th>
                  ))}
                  <th className="px-3 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400 text-right w-28">Actions</th>
                </tr>
              </thead>
              <tbody>
                {newRow && (
                  <tr className="border-b border-blue-900/50 bg-blue-500/5" data-testid="new-row">
                    {COLS.map((c) => (
                      <td key={c.key} className="px-2 py-1.5">
                        {renderCell(newRow, c, (k, v) => setNewRow((prev) => ({ ...prev, [k]: v })))}
                      </td>
                    ))}
                    <td className="px-3 py-1.5">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" onClick={saveNew} disabled={savingId === 'new'} data-testid="save-new-btn" className="h-7 px-2 bg-green-600 hover:bg-green-500 text-white">
                          <FloppyDisk size={14} weight="duotone" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setNewRow(null)} data-testid="cancel-new-btn" className="h-7 px-2 border-slate-700 text-slate-300 hover:bg-slate-800">
                          <X size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
                {filtered.map((s) => (
                  <tr key={s.id} data-testid={`row-${s.id}`} className="border-b border-slate-800/60 hover:bg-slate-800/20">
                    {COLS.map((c) => (
                      <td key={c.key} className={c.type === 'bool' ? 'px-3 py-1.5 text-center' : 'px-2 py-1.5'}>
                        {renderCell(s, c, (k, v) => updateCell(s.id, k, v))}
                      </td>
                    ))}
                    <td className="px-3 py-1.5">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          onClick={() => saveRow(s)}
                          disabled={!dirty[s.id] || savingId === s.id}
                          data-testid={`save-${s.id}`}
                          className={`h-7 px-2 ${dirty[s.id] ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-slate-800 text-slate-500'}`}
                        >
                          <FloppyDisk size={14} weight="duotone" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => deleteRow(s)} data-testid={`delete-${s.id}`} className="h-7 px-2 border-slate-700 text-red-400 hover:bg-red-500/10 hover:text-red-300">
                          <Trash size={14} weight="duotone" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default StudentsSheet;
